
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, MedewerkerResponse, MedewerkerInsert, MedewerkerUpdate } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useMedewerkerQueries = () => {
  const queryClient = useQueryClient();

  // Ophalen van medewerkers
  const medewerkerQuery = useQuery({
    queryKey: ['medewerkers'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('medewerkers')
          .select('*')
          .order('naam');
          
        if (error) throw error;
        
        return data as MedewerkerResponse[];
      } catch (error: any) {
        toast.error("Fout bij het ophalen van medewerkers", {
          description: error.message
        });
        return [];
      }
    },
  });

  // Toevoegen van een medewerker
  const addMutation = useMutation({
    mutationFn: async (newMedewerker: MedewerkerInsert) => {
      const { data, error } = await supabase
        .from('medewerkers')
        .insert(newMedewerker)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medewerkers'] });
      toast.success("Medewerker toegevoegd");
    },
    onError: (error: any) => {
      toast.error("Fout bij het toevoegen van medewerker", {
        description: error.message
      });
    }
  });

  // Bijwerken van een medewerker
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string, data: MedewerkerUpdate }) => {
      const { error } = await supabase
        .from('medewerkers')
        .update(data)
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medewerkers'] });
      toast.success("Medewerker bijgewerkt");
    },
    onError: (error: any) => {
      toast.error("Fout bij het bijwerken van medewerker", {
        description: error.message
      });
    }
  });

  // Verwijderen van een medewerker
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('medewerkers')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medewerkers'] });
      toast.success("Medewerker verwijderd");
    },
    onError: (error: any) => {
      toast.error("Fout bij het verwijderen van medewerker", {
        description: error.message
      });
    }
  });

  // Ophalen van toegewezen inpakkers voor een medewerker
  const getAssignedPackers = async (medewerkerId: string): Promise<string[]> => {
    try {
      const { data, error } = await supabase
        .from('medewerker_inpakker_relations')
        .select('inpakker_naam')
        .eq('medewerker_id', medewerkerId);
        
      if (error) {
        console.error("Error fetching assigned packers:", error);
        return [];
      }
      
      return data.map(relation => relation.inpakker_naam);
    } catch (error) {
      console.error("Error in getAssignedPackers:", error);
      return [];
    }
  };

  // Bijwerken van toegewezen inpakkers voor een medewerker
  const updateAssignedPackers = async (medewerkerId: string, packerNames: string[], medewerkerNaam: string = ""): Promise<boolean> => {
    try {
      // Get medewerker name if not provided
      if (!medewerkerNaam) {
        const { data, error } = await supabase
          .from('medewerkers')
          .select('naam')
          .eq('id', medewerkerId)
          .single();
          
        if (!error && data) {
          medewerkerNaam = data.naam;
        }
      }
      
      console.log("Updating assigned packers for medewerker:", medewerkerId, "name:", medewerkerNaam, "packers:", packerNames);
      
      // First remove existing relations
      const { error: deleteError } = await supabase
        .from('medewerker_inpakker_relations')
        .delete()
        .eq('medewerker_id', medewerkerId);
        
      if (deleteError) {
        console.error("Error removing packer relations:", deleteError);
        return false;
      }
      
      // Skip if no packers to add
      if (!packerNames.length) return true;
      
      // Insert new relations
      const relationsToInsert = packerNames.map(name => ({
        medewerker_id: medewerkerId,
        inpakker_naam: name,
        medewerker_naam: medewerkerNaam
      }));
      
      const { error: insertError } = await supabase
        .from('medewerker_inpakker_relations')
        .insert(relationsToInsert);
        
      if (insertError) {
        console.error("Error inserting packer relations:", insertError);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error in updateAssignedPackers:", error);
      return false;
    }
  };

  return {
    medewerkers: medewerkerQuery.data || [],
    isLoading: medewerkerQuery.isLoading,
    addMutation,
    updateMutation,
    deleteMutation,
    getAssignedPackers,
    updateAssignedPackers
  };
};
